// src/components/admin/GestionAbogados.js
import React, { useState, useEffect } from 'react';
import { Table, Button, Form, Modal, Alert, Spinner } from 'react-bootstrap';
import DatosMaestrosService from '../../services/datosMaestrosService';
import ModalConfirmacionCambioEstado from '../ModalConfirmacionCambioEstado';
import MenuAccionesFila from './MenuAccionesFila';
import PanelFiltros, { CampoFiltro } from '../PanelFiltros';
import '../../style/components/GestionDatosMaestros.css';
import useBreakpoint from '../../hooks/useBreakpoint';
import BottomSheet from '../mobile/BottomSheet';
import BotonFiltros from '../mobile/BotonFiltros';
import ChipsFiltros from '../mobile/ChipsFiltros';
import { coincideBusqueda } from '../../utils/textoComparable';

const GestionAbogados = () => {
    const [abogados, setAbogados] = useState([]);
    const [loading, setLoading] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [modalMode, setModalMode] = useState('create');
    const [selectedAbogado, setSelectedAbogado] = useState(null);
    const [formData, setFormData] = useState({
        nombre: '',
        apellido: '',
        matricula: '',
        telefono: '',
        email: ''
    });
    const [alert, setAlert] = useState({ show: false, message: '', variant: '' });
    // Error al guardar: se muestra dentro del modal, la alerta de la página queda tapada por él.
    const [errorFormulario, setErrorFormulario] = useState('');

    // Estados para el modal de confirmación de eliminación
    const [showModalEliminar, setShowModalEliminar] = useState(false);
    const [abogadoAEliminar, setAbogadoAEliminar] = useState(null);
    const [eliminando, setEliminando] = useState(false);
    
    // Filtros
    const [filterNombre, setFilterNombre] = useState('');
    const [filterApellido, setFilterApellido] = useState('');
    const [filterMatricula, setFilterMatricula] = useState('');
    const [sheetFiltrosAbierto, setSheetFiltrosAbierto] = useState(false);
    const { esMobile } = useBreakpoint();

    useEffect(() => {
        cargarAbogados();
    }, []);

    const cargarAbogados = async () => {
        setLoading(true);
        try {
            const response = await DatosMaestrosService.getAbogados();
            setAbogados(response.data);
        } catch (error) {
            mostrarAlerta('Error al cargar abogados', 'danger');
        } finally {
            setLoading(false);
        }
    };

    const mostrarAlerta = (message, variant) => {
        setAlert({ show: true, message, variant });
        setTimeout(() => setAlert({ show: false, message: '', variant: '' }), 4000);
    };

    const handleOpenModal = (mode, abogado = null) => {
        setModalMode(mode);
        if (mode === 'edit' && abogado) {
            setSelectedAbogado(abogado);
            setFormData({
                nombre: abogado.nombre,
                apellido: abogado.apellido,
                matricula: abogado.matricula || '',
                telefono: abogado.telefono || '',
                email: abogado.email || ''
            });
        } else {
            setSelectedAbogado(null);
            setFormData({
                nombre: '',
                apellido: '',
                matricula: '',
                telefono: '',
                email: ''
            });
        }
        setErrorFormulario('');
        setShowModal(true);
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setSelectedAbogado(null);
        setErrorFormulario('');
        setFormData({ nombre: '', apellido: '', matricula: '', telefono: '', email: '' });
    };

    // El controlador responde texto plano (p. ej. matrícula repetida) y el manejador global
    // un ApiError { code, message }.
    const mensajeDelBackend = (error, porDefecto) => {
        const datos = error?.response?.data;
        if (typeof datos === 'string' && datos.trim()) return datos;
        return datos?.message || porDefecto;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setErrorFormulario('');
        setLoading(true);

        try {
            if (modalMode === 'create') {
                await DatosMaestrosService.createAbogado(formData);
                mostrarAlerta('✅ Abogado creado exitosamente', 'success');
            } else {
                await DatosMaestrosService.updateAbogado(selectedAbogado.id, formData);
                mostrarAlerta('✅ Abogado actualizado exitosamente', 'success');
            }
            handleCloseModal();
            // Refresh automático de la lista
            await cargarAbogados();
        } catch (error) {
            setErrorFormulario(mensajeDelBackend(error, 'No se pudo guardar el abogado. Intente nuevamente.'));
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = (id) => {
        const abogado = abogados.find(a => a.id === id);
        setAbogadoAEliminar(abogado);
        setShowModalEliminar(true);
    };

    const confirmarEliminar = async () => {
        if (!abogadoAEliminar) return;
        
        setEliminando(true);
        setLoading(true);
        try {
            await DatosMaestrosService.deleteAbogado(abogadoAEliminar.id);
            mostrarAlerta('✅ Abogado eliminado exitosamente', 'success');
            setShowModalEliminar(false);
            setAbogadoAEliminar(null);
            // Refresh automático de la lista
            await cargarAbogados();
        } catch (error) {
            mostrarAlerta('❌ Error al eliminar el abogado', 'danger');
            setShowModalEliminar(false);
            setAbogadoAEliminar(null);
        } finally {
            setEliminando(false);
            setLoading(false);
        }
    };

    const cancelarEliminar = () => {
        setShowModalEliminar(false);
        setAbogadoAEliminar(null);
    };

    // Función para filtrar abogados
    const getAbogadosFiltrados = () => {
        let abogadosFiltrados = [...abogados];

        // Filtrar por nombre
        if (filterNombre.trim()) {
            abogadosFiltrados = abogadosFiltrados.filter(a => coincideBusqueda(a.nombre, filterNombre));
        }

        // Filtrar por apellido
        if (filterApellido.trim()) {
            abogadosFiltrados = abogadosFiltrados.filter(a => coincideBusqueda(a.apellido, filterApellido));
        }

        // Filtrar por matrícula
        if (filterMatricula.trim()) {
            abogadosFiltrados = abogadosFiltrados.filter(a => coincideBusqueda(a.matricula, filterMatricula));
        }

        return abogadosFiltrados;
    };

    return (
        <div className="gestion-datos-maestros">
            {alert.show && (
                <Alert variant={alert.variant} dismissible onClose={() => setAlert({ show: false, message: '', variant: '' })}>
                    {alert.message}
                </Alert>
            )}

            <div className="d-flex justify-content-end mb-3">
                <div className="d-flex flex-wrap gap-2">
                    <Button 
                        variant="outline-secondary" 
                        onClick={cargarAbogados}
                        disabled={loading}
                        className="shadow-sm"
                        title="Recargar lista"
                    >
                        <i className={`bi bi-arrow-clockwise ${loading ? 'spin' : ''}`}></i>
                    </Button>
                    <Button 
                        variant="primary" 
                        onClick={() => handleOpenModal('create')}
                        className="shadow-sm"
                    >
                        <i className="bi bi-plus-circle me-2"></i>
                        Nuevo Abogado
                    </Button>
                </div>
            </div>

            {/* Filtros */}
            {(() => {
                const limpiarFiltros = () => { setFilterNombre(''); setFilterApellido(''); setFilterMatricula(''); };
                const chipsFiltros = [
                    filterNombre.trim() !== '' && { key: 'nombre', label: filterNombre, onQuitar: () => setFilterNombre('') },
                    filterApellido.trim() !== '' && { key: 'apellido', label: filterApellido, onQuitar: () => setFilterApellido('') },
                    filterMatricula.trim() !== '' && { key: 'matricula', label: filterMatricula, onQuitar: () => setFilterMatricula('') },
                ].filter(Boolean);
                const resumen = `Mostrando ${getAbogadosFiltrados().length} de ${abogados.length} abogados`;

                const campoNombre = (
                    <CampoFiltro etiqueta="Nombre" icono="bi-person" htmlFor="filtro-abogado-nombre">
                        <Form.Control
                            id="filtro-abogado-nombre"
                            className="panel-filtros-input"
                            type="text"
                            placeholder="Ej: Juan…"
                            value={filterNombre}
                            onChange={(e) => setFilterNombre(e.target.value)}
                        />
                    </CampoFiltro>
                );
                const campoApellido = (
                    <CampoFiltro etiqueta="Apellido" icono="bi-person-vcard" htmlFor="filtro-abogado-apellido">
                        <Form.Control
                            id="filtro-abogado-apellido"
                            className="panel-filtros-input"
                            type="text"
                            placeholder="Ej: Pérez…"
                            value={filterApellido}
                            onChange={(e) => setFilterApellido(e.target.value)}
                        />
                    </CampoFiltro>
                );
                const campoMatricula = (
                    <CampoFiltro etiqueta="Matrícula" icono="bi-hash" htmlFor="filtro-abogado-matricula">
                        <Form.Control
                            id="filtro-abogado-matricula"
                            className="panel-filtros-input"
                            type="text"
                            placeholder="Ej: 12345…"
                            value={filterMatricula}
                            onChange={(e) => setFilterMatricula(e.target.value)}
                        />
                    </CampoFiltro>
                );

                return esMobile ? (
                    <div className="mb-3">
                        <BotonFiltros cantidad={chipsFiltros.length} onClick={() => setSheetFiltrosAbierto(true)} />
                        <ChipsFiltros chips={chipsFiltros} />
                        {sheetFiltrosAbierto && (
                            <BottomSheet
                                titulo="Filtros"
                                onCerrar={() => setSheetFiltrosAbierto(false)}
                                accionPrimaria={chipsFiltros.length > 0 ? { label: 'Limpiar filtros', onClick: limpiarFiltros } : undefined}
                            >
                                {campoNombre}
                                {campoApellido}
                                {campoMatricula}
                            </BottomSheet>
                        )}
                        <small className="d-block mt-2 text-muted">{resumen}</small>
                    </div>
                ) : (
                    <PanelFiltros hayFiltros={chipsFiltros.length > 0} onLimpiar={limpiarFiltros} resumen={resumen}>
                        {campoNombre}
                        {campoApellido}
                        {campoMatricula}
                    </PanelFiltros>
                );
            })()}

            {loading && abogados.length === 0 ? (
                <div className="text-center py-5">
                    <Spinner animation="border" variant="primary" />
                </div>
            ) : (
                <div className="table-responsive">
                    <Table hover className="datos-maestros-table">
                        <thead>
                            <tr>
                                <th>Nombre</th>
                                <th>Apellido</th>
                                <th>Matrícula</th>
                                <th>Teléfono</th>
                                <th>Email</th>
                                <th className="text-end col-acciones"><span className="visually-hidden">Acciones</span></th>
                            </tr>
                        </thead>
                        <tbody>
                            {getAbogadosFiltrados().length === 0 ? (
                                <tr>
                                    <td colSpan="6" className="text-center text-muted py-4">
                                        {abogados.length === 0 
                                            ? 'No hay abogados registrados' 
                                            : 'No se encontraron abogados con los filtros aplicados'}
                                    </td>
                                </tr>
                            ) : (
                                getAbogadosFiltrados().map((abogado) => (
                                    <tr key={abogado.id}>
                                        <td className="fw-semibold">{abogado.nombre}</td>
                                        <td className="fw-semibold">{abogado.apellido}</td>
                                        <td>
                                            <span className="badge bg-success">{abogado.matricula || '-'}</span>
                                        </td>
                                        <td>{abogado.telefono || '-'}</td>
                                        <td>{abogado.email || '-'}</td>
                                        <td className="text-end">
                                            <MenuAccionesFila acciones={[
                                                { etiqueta: 'Editar', icono: 'bi-pencil', onClick: () => handleOpenModal('edit', abogado) },
                                                { etiqueta: 'Eliminar', icono: 'bi-trash', peligro: true, onClick: () => handleDelete(abogado.id) },
                                            ]} />
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </Table>
                </div>
            )}

            <Modal show={showModal} onHide={handleCloseModal} centered>
                <Modal.Header closeButton>
                    <Modal.Title>
                        {modalMode === 'create' ? 'Nuevo Abogado' : 'Editar Abogado'}
                    </Modal.Title>
                </Modal.Header>
                <Form onSubmit={handleSubmit}>
                    <Modal.Body>
                        {errorFormulario && (
                            <Alert variant="danger" className="mb-3">
                                {errorFormulario}
                            </Alert>
                        )}
                        <Form.Group className="mb-3">
                            <Form.Label>Nombre *</Form.Label>
                            <Form.Control
                                type="text"
                                value={formData.nombre}
                                onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
                                required
                                placeholder="Ej: Juan"
                            />
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>Apellido *</Form.Label>
                            <Form.Control
                                type="text"
                                value={formData.apellido}
                                onChange={(e) => setFormData({ ...formData, apellido: e.target.value })}
                                required
                                placeholder="Ej: Pérez"
                            />
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>Matrícula</Form.Label>
                            <Form.Control
                                type="text"
                                value={formData.matricula}
                                onChange={(e) => setFormData({ ...formData, matricula: e.target.value })}
                                placeholder="Ej: 12345"
                            />
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>Teléfono</Form.Label>
                            <Form.Control
                                type="text"
                                value={formData.telefono}
                                onChange={(e) => setFormData({ ...formData, telefono: e.target.value })}
                                placeholder="Ej: 221-123-4567"
                            />
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>Email</Form.Label>
                            <Form.Control
                                type="email"
                                value={formData.email}
                                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                                placeholder="Ej: juan.perez@estudio.com"
                            />
                        </Form.Group>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={handleCloseModal}>
                            Cancelar
                        </Button>
                        <Button variant="primary" type="submit" disabled={loading}>
                            {loading ? <Spinner animation="border" size="sm" /> : 'Guardar'}
                        </Button>
                    </Modal.Footer>
                </Form>
            </Modal>

            {/* Modal de confirmación para eliminar abogado */}
            <ModalConfirmacionCambioEstado
                isOpen={showModalEliminar}
                onClose={cancelarEliminar}
                onConfirm={confirmarEliminar}
                titulo="Eliminar Abogado"
                mensaje={
                    abogadoAEliminar
                        ? `¿Está seguro de eliminar al abogado "${abogadoAEliminar.nombre} ${abogadoAEliminar.apellido}"?\n\nEsta acción no se puede deshacer.`
                        : '¿Está seguro de que desea eliminar este abogado?'
                }
                cargando={eliminando}
                textoBotonConfirmar="Eliminar"
                textoBotonCancelar="Cancelar"
                variantBotonConfirmar="danger"
            />
        </div>
    );
};

export default GestionAbogados;
